import { useCallback, useEffect, useMemo } from 'react';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { FormError } from 'components/uikit/FormError';
import { Input } from 'components/uikit/inputs/Input';
import { Button } from 'components/uikit/buttons/Button';
import { ButtonColor } from 'components/uikit/buttons/Button';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { Field } from 'components/uikit/Field';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import styles from './EditItemModal.module.scss';
import { useModal } from 'components/uikit/modal/useModal';
import { TreeItems } from 'dnd-kit-sortable-tree';
import {
  getItemById,
  isItemExported,
  setItemUnexported,
} from 'helpers/documents/documentsObserver';
import { Loading } from 'components/uikit/suspense/Loading';
import { nameEndRegex, nameStartRegex } from '../document-list-helper';
import { BaseNode } from 'helpers/documents/types/base';

type EditItemModalProps = {
  id: string;
  name: string;
  onFolderSubmit: (name: string) => void;
  onFileSubmit: (name: string) => void;
  onClose: () => void;
  folders: TreeItems<BaseNode> | BaseNode[];
};

type EditItemFields = {
  name?: string;
};

export const EditItemModal: React.FC<EditItemModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.EditFolderModal');
  const foldersTranslation = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree',
  );
  const buttonTitle = useScopedTranslation('Button.Title');
  const enumsI18n = useScopedTranslation('Enums');
  const confirmModal = useModal();
  const modalTranslation = useScopedTranslation('Component.CommonModals');

  const { folders } = props;
  const currentItem = getItemById(props.id, folders);

  const form = useAdvancedForm<EditItemFields>(
    useCallback(
      async (data) => {
        props.onClose();

        const shouldConfirm = isItemExported(currentItem!);
        let confirmed = false;

        if (shouldConfirm)
          confirmed = await confirmModal.showConfirm({
            title: modalTranslation.t('confirm_change'),
            text: modalTranslation.t('confirm_change_message'),
          });

        if (confirmed) await setItemUnexported(currentItem!);

        if (confirmed || !shouldConfirm) {
          switch (currentItem?.type) {
            case 'Folder':
              props.onFolderSubmit(data!.name!);
              break;
            case 'Document':
            case 'Template':
              props.onFileSubmit(data!.name!);
              break;
          }
        }
      },
      [folders],
    ),
    {
      defaultValues: useMemo(
        () => ({
          name: props.name,
        }),
        [props.name],
      ),
    },
  );
  useEffect(() => {
    setTimeout(() => {
      form.setFocus('name');
    });
  }, [form]);

  return (
    <Loading loading={form.formState.isSubmitting}>
      <form onSubmit={form.handleSubmitDefault}>
        <Field
          title={i18n.t('name_field', {
            itemType: enumsI18n.t(`NodeType.${currentItem!.type}`),
          })}
        >
          <Input
            {...form.register('name', {
              ...requiredRule(),
              /*Folder must have correct name to have possibility of export to SharePoint*/
              validate: {
                hasSpecialCharStart: (value) =>
                  currentItem?.type !== 'Folder' ||
                  nameStartRegex.test(value!) ||
                  foldersTranslation.t('TreeItem.unacceptable_characters'),
                hasSpecialCharEnd: (value) =>
                  currentItem?.type !== 'Folder' ||
                  nameEndRegex.test(value!) ||
                  foldersTranslation.t('TreeItem.unacceptable_characters_end'),
              },
            })}
            errorText={form.formState.errors.name?.message}
          />
        </Field>
        <FormError>{form.overallError}</FormError>

        <div className={styles.footer}>
          <Button
            title={buttonTitle.t('cancel')}
            color={ButtonColor.Secondary}
            className={styles.button}
            onClick={async () => {
              props.onClose();
            }}
          />
          <Button
            type={'submit'}
            title={buttonTitle.t('save')}
            color={ButtonColor.Primary}
            className={styles.button}
          />
        </div>
      </form>
    </Loading>
  );
};
